import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button/index.tsx';
import { CopyToClipboardButton } from '@sorare/core/src/atoms/buttons/CopyToClipboardButton/index.tsx';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons/index.tsx';
import { iconDuplicate } from '@sorare/core/src/atoms/icons/system/iconDuplicate.ts';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyL } from '@sorare/core/src/atoms/typography';
import { SocialShare } from '@sorare/core/src/components/user/SocialShare/index.tsx';
import {
  socialShareEventContext,
  socialShareEventName,
} from '@sorare/core/src/lib/events.ts';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import classNames from 'classnames';
import { InviteTermsApplyLink } from '../InviteTermsApplyLink/index.tsx';

const ReferralSection = styled(Vertical)`
  padding: var(--double-unit);
  border-radius: var(--unit);
  width: 100%;
  &:not(.forceMobileLayout) {
    @media ${laptopAndAbove} {
      background-color: var(--c-nd-100);
      padding: var(--unit);
      flex-direction: row;
    }
  }
`;
const ReferralUrlText = styled(BodyL)`
  flex: 1;
  white-space: nowrap;
`;
const FadedSpan = styled.span`
  color: var(--c-nd-600);
`;

const StyledCopyToClipboardButton = styled(CopyToClipboardButton)`
  flex: 1;
  display: flex;
  & > * {
    flex: 1;
    height: 48px;
    padding: var(--unit);
  }
  background-color: var(--c-nd-100);
  border-radius: var(--unit);
  &:not(.forceMobileLayout) {
    @media ${laptopAndAbove} {
      background-color: transparent;
    }
  }
`;

type Props = {
  referrerCode: string | null | undefined;
  friendsRemaining: number;
  generating: boolean;
  shareMessage?: string;
  forceMobileLayout?: boolean;
};

export const InviteCTA = ({
  referrerCode,
  friendsRemaining,
  generating,
  shareMessage,
  forceMobileLayout = false,
}: Props) => (
  <Vertical gap={0.5} center style={{ width: '100%' }}>
    <ReferralSection className={classNames({ forceMobileLayout })}>
      {!!referrerCode && (
        <StyledCopyToClipboardButton
          textToCopy={referrerCode}
          className={classNames({ forceMobileLayout })}
        >
          <SBHorizontal style={{ flex: 1 }}>
            <ReferralUrlText
              bold
              className="text-center"
              color="var(--c-white)"
            >
              {referrerCode}
            </ReferralUrlText>
            <FontAwesomeIcon icon={iconDuplicate} color="var(--c-white)" />
          </SBHorizontal>
        </StyledCopyToClipboardButton>
      )}
      <SocialShare
        message={shareMessage}
        renderButton={({ onClick }) => (
          <Button
            size="large"
            color="primary"
            disabled={generating || !referrerCode}
            onClick={onClick}
          >
            <FormattedMessage
              defaultMessage="Invite friend <span>({friendsRemaining, number} left)</span>"
              values={{
                friendsRemaining,
                span: chunks => <FadedSpan>{chunks}</FadedSpan>,
              }}
            />
          </Button>
        )}
        trackingEventName={socialShareEventName.SHARE_INVITE_FRIENDS_SERIES}
        trackingEventContext={socialShareEventContext.SERIES_INVITE_FRIENDS}
      />
    </ReferralSection>
    <InviteTermsApplyLink />
  </Vertical>
);
